<template>
  <v-container fluid class="pa-0">
    <v-tabs
    class="card-tabs sub-tabs"
    v-model="currentTab">
      <v-tab
      v-for="tab in tabs"
      :key="tab.id">
        <v-icon small class="mr-2">{{ tab.icon }}</v-icon>
        <span>
          {{ $t(tab.name) }}
        </span>
      </v-tab>
    </v-tabs>
    <v-window
    v-model="currentTab">
      <v-window-item>
        <AccountProducts
        :account="account"
        :crud="crud"
        @products-chosen="onAccountProductsChosen"
        @fallback-product-chosen="onFallbackProductChosen"
        @exclude-from-expansion-changed="onExcludeFromExpansionChanged"/>
      </v-window-item>

      <v-window-item>
        <div :style="logLengthsRowStyle">
          <v-row no-gutters>
            <v-col cols="12" xl="5" :class="logLengthsColumnClass">
              <AccountLogLengths
              :crud="crud"
              :account="account"
              :selectedLengths="selectedLogLengths"
              @update:selectedLengths="handleSelectedLogLengthsChange"/>
            </v-col>
            <v-col cols="12" xl="7" :class="lengthGroupsColumnClass">
              <AccountLengthGroups
              :crud="crud"
              v-if="renderLengthGroups"
              :accountId="accountId"
              :selectedLengths="selectedLogLengths"
              @account-length-group-mutated="handleLengthGroupMutated"/>
            </v-col>
          </v-row>
        </div>
      </v-window-item>

      <v-window-item>
        <v-row no-gutters>
          <v-col md="auto" :style="{ width: $vuetify.breakpoint.mdAndUp ? '700px' : '100%' }">
            <AccountDiameterGroups
            :crud="crud"
            :accountId="accountId"
            @account-diameter-group-mutated="handleDiameterGroupMutated"/>
          </v-col>
        </v-row>
      </v-window-item>

      <v-window-item>
        <v-row no-gutters>
          <v-col>
            <AccountSpeciesGroups
            v-if="speciesGroupsRendered"
            :crud="crud"
            :account="account"
            @account-species-group-mutated="handleSpeciesGroupMutated"/>
          </v-col>
        </v-row>
      </v-window-item>

      <v-window-item>
        <AccountRateSheetDefinitions
        v-if="rateSheetDefinitionsRendered"
        :crud="crud"
        :account="account"
        @volume-pricing-mode-changed="handleVolumePricingModeChanged"/>
      </v-window-item>
    </v-window>
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex'

const tabs = {
  Product: {
    id: 0,
    name: 'product',
    icon: 'mdi-pine-tree-variant'
  },
  Length: {
    id: 1,
    name: 'length',
    icon: 'mdi-ruler'
  },
  Diameter: {
    id: 2,
    name: 'diameter',
    icon: 'mdi-diameter'
  },
  Species: {
    id: 3,
    name: 'species',
    icon: 'mdi-leaf-maple'
  },
  Rates: {
    id: 4,
    name: 'rates',
    icon: 'mdi-currency-usd'
  }
}

export default {
  name: 'AccountScalingSpecs',

  components: {
    AccountLogLengths: () => import('@/components/account/account-scaling/log-lengths/AccountLogLengths.vue'),
    AccountLengthGroups: () => import('@/components/account/account-scaling/length-groups/AccountLengthGroups.vue'),
    AccountDiameterGroups: () => import('@/components/account/account-scaling/diameter-groups/AccountDiameterGroups.vue'),
    AccountSpeciesGroups: () => import('@/components/account/account-scaling/species-groups/AccountSpeciesGroups.vue'),
    AccountRateSheetDefinitions: () => import('@/components/account/account-scaling/rate-sheet-definitions/AccountRateSheetDefinitions.vue'),
    AccountProducts: () => import('@/components/account/account-scaling/products/AccountProducts.vue')
  },

  props: {
    crud: {
      type: Boolean,
      required: false,
      default: false
    },

    account: {
      type: Object,
      required: true
    }
  },

  data: () => ({
    volumePricingMode: 0,
    tabs: tabs,
    currentTab: tabs.Product.id,
    speciesGroupsRendered: false,
    rateSheetDefinitionsRendered: false,
    selectedLogLengths: []
  }),

  computed: {
    ...mapGetters('account-scaling', ['accountLogLengths', 'accountLengthGroups', 'accountDiameterGroups', 'accountSpeciesGroups']),
    ...mapGetters('dialog', ['topDialogState']),

    accountId () {
      return this.account?.accountId
    },

    logLengthsColumnClass () {
      return this.$vuetify.breakpoint.xl ? 'pr-1' : ''
    },

    lengthGroupsColumnClass () {
      return this.$vuetify.breakpoint.xl ? 'pl-1' : 'pt-2'
    },

    renderLengthGroups () {
      return !this.isEmpty(this.accountLogLengths)
    },

    logLengthsRowStyle () {
      return {
        height: `calc(${this.topDialogState?.fullscreen || this.$vuetify.breakpoint.mdAndDown ? 100 : 90}vh - 250px)`,
        overflowY: 'auto'
      }
    }
  },

  watch: {
    async currentTab (tab) {
      switch (tab) {
        case tabs.Species.id:
          this.speciesGroupsRendered = true
          break
        case tabs.Rates.id:
          this.rateSheetDefinitionsRendered = true
          break
      }
    }
  },

  methods: {
    isEmpty (array) {
      return !(array?.length > 0)
    },

    handleVolumePricingModeChanged (volumePricingMode) {
      this.$emit('volume-pricing-mode-changed', volumePricingMode)
    },

    handleLengthGroupMutated () {
      this.speciesGroupsRendered = false
      this.rateSheetDefinitionsRendered = false
    },

    handleDiameterGroupMutated () {
      this.speciesGroupsRendered = false
      this.rateSheetDefinitionsRendered = false
    },

    handleSpeciesGroupMutated () {
      this.rateSheetDefinitionsRendered = false
    },

    onFallbackProductChosen (product) {
      this.$emit('fallback-product-chosen', product)
    },

    onExcludeFromExpansionChanged (exclude) {
      this.$emit('exclude-from-expansion-changed', exclude)
    },

    onAccountProductsChosen (products) {
      this.$emit('products-chosen', products)
    },

    handleSelectedLogLengthsChange (lengths) {
      this.selectedLogLengths = lengths
    }
  }
}
</script>
